import React, { Fragment, useState, useEffect } from "react";
import { closeModal } from "../../store/modal/modalReducer";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faImages } from "@fortawesome/pro-regular-svg-icons";
import { Dialog, Transition } from "@headlessui/react";
import ErrorBoundary from "../../containers/ErrorBoundary";
import Select from "react-select";
import CustomButton from "../Helpers/CustomButton";

export default function ModalSendGallery(props) {
  const dispatch = useDispatch();
  const [open] = useState(true);
  const [galleries, setGalleries] = useState([]);
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const { current_staff } = useSelector((state) => state.auth);

  useEffect(() => {
    fetchGalleries();
  }, []);

  const fetchGalleries = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}galleries`,
      );
      setGalleries(response.data.data || []);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching galleries:", error);
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!selectedGallery) {
      alert("Please select a gallery");
      return;
    }

    setSending(true);

    try {
      const message = `[gallery id='${selectedGallery.id}']`;

      await axios({
        method: "post",
        url: `${process.env.REACT_APP_API_URL}send_message`,
        data: {
          booking_id: props.booking_id,
          message: message,
          from: current_staff.id,
        },
      });

      dispatch(closeModal());

      if (props.onSuccess) {
        props.onSuccess();
      }
    } catch (error) {
      console.error("Error sending gallery:", error);
      alert("Failed to send gallery. Please try again.");
      setSending(false);
    }
  };

  const galleryOptions = galleries.map((gallery) => ({
    value: gallery.id,
    label: `${gallery.display_name} (${gallery.photo_count} photos)`,
    data: gallery,
  }));

  const groupedOptions = galleries.reduce((acc, gallery) => {
    const propertyName = gallery.property_name || "No Property";
    if (!acc[propertyName]) {
      acc[propertyName] = [];
    }
    acc[propertyName].push({
      value: gallery.id,
      label: gallery.unit_type_name
        ? `${gallery.name || propertyName} - ${gallery.unit_type_name} (${gallery.photo_count} photos)`
        : `${gallery.name || propertyName} (${gallery.photo_count} photos)`,
      data: gallery,
    });
    return acc;
  }, {});

  const formattedOptions = Object.keys(groupedOptions).map((propertyName) => ({
    label: propertyName,
    options: groupedOptions[propertyName],
  }));

  return (
    <ErrorBoundary>
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          static
          className="fixed z-50 inset-0 overflow-y-auto"
          open={open}
          onClose={() => dispatch(closeModal())}
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </Transition.Child>

            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-visible shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                  <button
                    type="button"
                    className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                    onClick={() => dispatch(closeModal())}
                  >
                    <span className="sr-only">Close</span>
                    <FontAwesomeIcon icon={faTimes} className="h-6 w-6" />
                  </button>
                </div>
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                    <FontAwesomeIcon
                      icon={faImages}
                      className="h-6 w-6 text-blue-600"
                    />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <Dialog.Title
                      as="h3"
                      className="text-lg leading-6 font-medium text-gray-900"
                    >
                      Send Gallery
                    </Dialog.Title>
                    <div className="mt-4">
                      {loading ? (
                        <div className="text-center py-4">
                          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                          <p className="mt-2 text-sm text-gray-500">
                            Loading galleries...
                          </p>
                        </div>
                      ) : galleries.length === 0 ? (
                        <div className="text-center py-4">
                          <p className="text-sm text-gray-500">
                            No galleries available. Please create a gallery
                            first.
                          </p>
                        </div>
                      ) : (
                        <>
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Select Gallery
                            </label>
                            <Select
                              options={formattedOptions}
                              value={
                                selectedGallery
                                  ? {
                                      value: selectedGallery.id,
                                      label: `${selectedGallery.display_name} (${selectedGallery.photo_count} photos)`,
                                    }
                                  : null
                              }
                              onChange={(option) =>
                                setSelectedGallery(option?.data || null)
                              }
                              placeholder="Choose a gallery..."
                              isClearable
                              className="react-select-container"
                              classNamePrefix="react-select"
                            />
                          </div>

                          {selectedGallery && (
                            <div className="bg-gray-50 rounded-md p-3 mb-4">
                              <p className="text-sm text-gray-700">
                                <strong>Property:</strong>{" "}
                                {selectedGallery.property_name}
                              </p>
                              {selectedGallery.unit_type_name && (
                                <p className="text-sm text-gray-700">
                                  <strong>Unit Type:</strong>{" "}
                                  {selectedGallery.unit_type_name}
                                </p>
                              )}
                              <p className="text-sm text-gray-700">
                                <strong>Photos:</strong>{" "}
                                {selectedGallery.photo_count}
                              </p>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <CustomButton
                    onClick={handleSend}
                    disabled={!selectedGallery || sending || loading}
                    loading={sending}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {sending ? "Sending..." : "Send Gallery"}
                  </CustomButton>
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                    onClick={() => dispatch(closeModal())}
                    disabled={sending}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </ErrorBoundary>
  );
}
